/** @prettier */

/**
 * 規約
 * ・関数の名前はAPIの「operationId」を使用すること
 * ・型は必ず指定すること
 * ・ここでサニタイズされた文字を戻すこと
 */

import {API, Auth} from 'aws-amplify';
import * as api from '../Type/api';
import {
  sanitizeRestore,
  sanitizeRestoreObject,
} from '../functions/convertString';

/**
 * undefinedをオブジェクトから探して取り除く
 * @param obj {object} 対象オブジェクト
 * @returns undefinedを削除したオブジェクト
 */
const _delUndefinedObjFunc = (obj: object) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

/**
 * サイトの情報を返します
 * - 1度で最大「max-result」個のサイトの情報をサイト作成順で返します。 「item-page」を使用すると指定したページのサイト情報を返します。
 * - GET /compositions/sites
 * @param query {apiQueryParameters.getSiteInfo} パラメータ
 * @returns
 */
export const getCompositionsSites = async (
  query: api.reqParameters.getCompositionsSites
) => {
  const apiName = 'my-api';
  const path = '/compositions/sites';

  //API要求クエリセット
  //prettier-ignore
  const apiQuery: api.reqParameters.getCompositionsSites = {
    'item-page'     : query['item-page'    ],
    'max-result'    : query['max-result'   ],
    'fields'        : query['fields'       ],
    'sort'          : query['sort'         ],
    'user-authority' : query['user-authority'],
    'device-type'    : query['device-type'   ],
  };

  //API要求パラメータセット（クエリのundefinedは取り除く）
  const apiParams: api.req.getCompositionsSites = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    queryStringParameters: _delUndefinedObjFunc(apiQuery),
  };

  //実行
  try {
    const response: api.res.getCompositionsSites = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getCompositionsSites = JSON.parse(
      JSON.stringify(response)
    );
    restoredResponse['site-informations'] = response['site-informations'].map(
      info => {
        return sanitizeRestoreObject(
          info
        ) as api.resParameters.siteInformations;
      }
    );

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のサイトの情報を返します。
 * - GET /compositions/sites/{siteid}
 * @param siteId 取得するサイトのID
 * @returns 特定のサイトの情報
 */
export const getCompositionsSitesBySiteID = async (siteId: string) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}`;

  //API要求パラメータセット
  const apiParams: api.req.getCompositionsSitesBySiteID = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行
  try {
    const response: api.res.getCompositionsSitesBySiteID = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getCompositionsSitesBySiteID;
  } catch (error) {
    throw error;
  }
};

/**
 * サイトを追加します。
 * - POST /compositions/sites
 * @param body 追加したいサイトの情報
 * @returns 追加したサイトの情報
 */
export const postCompositionsSites = async (
  body: api.reqParameters.postCompositionsSites
) => {
  const apiName = 'my-api';
  const path = '/compositions/sites';

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.postCompositionsSites = {
    headers: {
      'Authorization': `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
      'x-api-key': process.env.REACT_APP_apiKeySite,
    },
    body: tempBody,
  };

  //実行
  try {
    const response: api.res.postCompositionsSites = await API.post(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.postCompositionsSites;
  } catch (error) {
    throw error;
  }
};

/**
 * サイトの情報を一括で修正します。
 * - PUT /compositions/sites/{siteid}
 * @param siteId 修正するサイトのID
 * @param body 修正したサイトの情報
 * @returns 実行結果
 */
export const putCompositionsSites = async (
  siteId: string,
  body: api.reqParameters.putCompositionsSites
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.putCompositionsSites = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.put(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * サイトの情報を個別に修正します。
 * - PATCH /compositions/sites/{siteid}
 * @param siteId 修正するサイトのID
 * @param body 修正したサイトの情報
 * @returns 実行結果
 */
export const patchCompositionsSites = async (
  siteId: string,
  body: api.reqParameters.patchCompositionsSites
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.patchCompositionsSites = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.patch(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * サイトの情報を削除します。
 * - DELETE /compositions/sites/{siteid}
 * @param siteId 削除するサイトのID
 * @returns 実行結果
 */
export const deleteCompositionsSites = async (siteId: string) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}`;

  //API要求パラメータセット
  const apiParams: api.req.deleteCompositionsSites = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.del(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 仮登録されたゲートウェイの情報を返します。
 * - GET /compositions/sites/{siteid}/gateway/activation
 * @param siteId 取得したいゲートウェイが登録されているサイトのID
 * @param query パラメータ
 * @returns 仮登録されたゲートウェイの情報
 */
export const getCompositionsActivation = async (
  siteId: string,
  query?: api.reqParameters.getCompositionsActivation
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/activation`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getCompositionsActivation;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.getCompositionsActivation = {
      'item-page'  : query['item-page'],
      'max-result' : query['max-result'  ],
    };

    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
    };
  }

  //実行
  try {
    const response: api.res.getCompositionsActivation = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getCompositionsActivation = JSON.parse(
      JSON.stringify(response)
    );
    //prettier-ignore
    restoredResponse['gateway-informations'] = response['gateway-informations'].map(info => {
      return sanitizeRestoreObject(info) as api.resParameters.activationGatewayInformations;
    });

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * ゲートウェイを仮登録します。
 * - POST /compositions/sites/{siteid}/gateway/activation
 * @param siteId 仮登録したいゲートウェイを登録するサイトのID
 * @param query パラメータ
 * @returns 証明書をダウンロードするパス
 */
export const postCompositionsActivation = async (
  siteId: string,
  query: api.reqParameters.postCompositionsActivation
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/activation`;

  //API要求クエリセット（クエリのundefinedは取り除く）
  //prettier-ignore
  const apiQuery = _delUndefinedObjFunc({
    'gateway-name' : query['gateway-name'],
    'agency-code'   : query['agency-code'],
    'gateway-id'   : query['gateway-id'],
  }) as api.reqParameters.postCompositionsActivation;

  //API要求パラメータセット
  const apiParams: api.req.postCompositionsActivation = {
    headers: {
      'Authorization': `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
      'x-api-key': process.env.REACT_APP_apiKeyGateway,
    },
    queryStringParameters: apiQuery,
  };

  //実行
  try {
    const response: api.res.postCompositionsActivation = await API.post(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.postCompositionsActivation;
  } catch (error) {
    throw error;
  }
};

/**
 * 仮登録されたゲートウェイの情報を削除します。
 * - DELETE /compositions/sites/{siteid}/gateway/activation
 * @param siteId 削除したいゲートウェイが登録されているサイトのID
 * @param query パラメータ
 * @returns 実行結果
 */
export const deleteCompositionsActivation = async (
  siteId: string,
  query: api.reqParameters.deleteCompositionsActivation
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/activation`;

  //API要求クエリセット（クエリのundefinedは取り除く）
  //prettier-ignore
  const apiQuery = _delUndefinedObjFunc({
    'gateway-name' : query['gateway-name'],
  }) as api.reqParameters.deleteCompositionsActivation;

  //API要求パラメータセット
  const apiParams: api.req.deleteCompositionsActivation = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    queryStringParameters: apiQuery,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.del(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * ゲートウェイの情報を返します。
 * - GET /compositions/sites/{siteid}/gateway
 * @param siteId 取得したいゲートウェイが登録されているサイトのID
 * @param query パラメータ
 * @returns ゲートウェイの情報
 */
export const getCompositionsGateway = async (
  siteId: string,
  query?: api.reqParameters.getCompositionsGateway
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getCompositionsGateway;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.getCompositionsGateway = {
      'fields'     : query['fields'    ],
      'item-page'  : query['item-page' ],
      'max-result' : query['max-result'],
    };

    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
    };
  }

  //実行
  try {
    const response: api.res.getCompositionsGateway = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getCompositionsGateway = JSON.parse(
      JSON.stringify(response)
    );
    //prettier-ignore
    restoredResponse['gateway-informations'] = response['gateway-informations'].map(info => {
      return sanitizeRestoreObject(info) as api.resParameters.gatewayInformations;
    });

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のゲートウェイの情報を返します。
 * - GET /compositions/sites/{siteid}/gateway/{gatewayid}
 * @param siteId 取得したいゲートウェイが登録されているサイトのID
 * @param gatewayId 取得したいゲートウェイのID
 * @returns 特定のゲートウェイの情報
 */
export const getCompositionsGatewayByGatewayID = async (
  siteId: string,
  gatewayId: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}`;

  //API要求パラメータセット
  const apiParams: api.req.getCompositionsGatewayByGatewayID = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行
  try {
    const response: api.res.getCompositionsGatewayByGatewayID = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getCompositionsGatewayByGatewayID;
  } catch (error) {
    throw error;
  }
};

/**
 * ゲートウェイの情報を修正します。
 * - PUT /compositions/sites/{siteid}/gateway/{gatewayid}
 * @param siteId 修正したいゲートウェイが登録されているサイトのID
 * @param gatewayId 修正したいゲートウェイのID
 * @param body 修正したゲートウェイの情報
 * @returns 実行結果
 */
export const putCompositionsGateway = async (
  siteId: string,
  gatewayId: string,
  body: api.reqParameters.putCompositionsGateway
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.putCompositionsGateway = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.put(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * ゲートウェイの情報を削除します。
 * - DELETE /compositions/sites/{siteid}/gateway/{gatewayid}
 * @param siteId 削除したいゲートウェイが登録されているサイトのID
 * @param gatewayId 削除したいゲートウェイのID
 * @returns 実行結果
 */
export const deleteCompositionsGateway = async (
  siteId: string,
  gatewayId: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}`;

  //API要求パラメータセット
  const apiParams: api.req.deleteCompositionsGateway = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.del(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のゲートウェイの機器毎の通信状態を返します。
 * - GET /communication/sites/{siteid}/gateway/{gatewayid}
 * @param siteId 取得したいゲートウェイが登録されているサイトのID
 * @param gatewayId 取得したいゲートウェイのID
 * @returns 特定のゲートウェイの機器毎の通信状態
 */
export const getCommunicationGateway = async (
  siteId: string,
  gatewayId: string
) => {
  const apiName = 'my-api';
  const path = `/communication/sites/${siteId}/gateway/${gatewayId}`;

  //API要求パラメータセット
  const apiParams: api.req.getCommunicationGateway = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行
  try {
    const response:
      | api.resParameters.slUnitCommunications
      | api.resParameters.rtUnitCommunications = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(JSON.parse(JSON.stringify(response))) as
      | api.resParameters.slUnitCommunications
      | api.resParameters.rtUnitCommunications;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のゲートウェイの通信テストの開始時間を更新します。
 * - POST /communication/sites/{siteid}/gateway/{gatewayid}
 * @param siteId サイトのID
 * @param gatewayId ID
 * @returns 結果
 */
export const postCommunicationGateway = async (
  siteId: string,
  gatewayId: string
) => {
  const apiName = 'my-api';
  const path = `/communication/sites/${siteId}/gateway/${gatewayId}`;

  //API要求パラメータセット
  const apiParams: api.req.getCommunicationGateway = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.post(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 室内外機の情報を返します。
 * - GET /compositions/sites/{siteid}/gateway/{gatewayid}/sl-unit
 * @param siteId 取得したい室内外機が登録されているサイトのID
 * @param gatewayId 取得したい室内外機が登録されているゲートウェイのID
 * @param query パラメータ
 * @returns 室内外機の情報
 */
export const getCompositionsSLUnit = async (
  siteId: string,
  gatewayId: string,
  query?: api.reqParameters.getCompositionsGateway
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/sl-unit`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getCompositionsSLUnit;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.getCompositionsSLUnit = {
      'fields'     : query['fields'    ],
      'item-page'  : query['item-page' ],
      'max-result' : query['max-result'],
    };

    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
    };
  }

  //実行
  try {
    const response: api.res.getCompositionsSLUnit = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getCompositionsSLUnit = JSON.parse(
      JSON.stringify(response)
    );
    //prettier-ignore
    restoredResponse['outdoor-unit-informations'] = response['outdoor-unit-informations'].map(info => {
      return sanitizeRestoreObject(info) as api.resParameters.slUnitInformations;
    });

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定の室外機の情報を返します。
 * - GET /compositions/sites/{siteid}/gateway/{gatewayid}/sl-unit/{outdoorid}
 * @param siteId 取得したい室内外機が登録されているサイトのID
 * @param gatewayId 取得したい室内外機が登録されているゲートウェイのID
 * @param ouId 取得したい室外機のID
 * @returns 特定の室外機の情報
 */
export const getCompositionsSLUnitByOUID = async (
  siteId: string,
  gatewayId: string,
  ouId: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/sl-unit/${ouId}`;

  //API要求パラメータセット
  const apiParams: api.req.getCompositionsSLUnitByOUID = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行
  try {
    const response: api.res.getCompositionsSLUnitByOUID = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getCompositionsSLUnitByOUID;
  } catch (error) {
    throw error;
  }
};

/**
 * 室外機を追加します。
 * - POST /compositions/sites/{siteid}/gateway/{gatewayid}/sl-unit
 * @param siteId 追加したい室外機が登録されているサイトのID
 * @param gatewayId 追加したい室外機が登録されているゲートウェイのID
 * @param body 追加したい室外機の情報
 * @returns 追加した室外機の情報
 */
export const postCompositionsSLUnit = async (
  siteId: string,
  gatewayId: string,
  body: api.reqParameters.postCompositionsSLUnit
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/sl-unit`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.postCompositionsSLUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行
  try {
    const response: api.res.postCompositionsSLUnit = await API.post(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.postCompositionsSLUnit;
  } catch (error) {
    throw error;
  }
};

/**
 * 室内外機の情報を修正します。
 * - PATCH /compositions/sites/{siteid}/gateway/{gatewayid}/sl-unit/{outdoorid}
 * @param siteId 修正したい室内外機が登録されているサイトのID
 * @param gatewayId 修正したい室内外機が登録されているゲートウェイのID
 * @param ouId 修正したい室外機のID
 * @param body 修正した室内外機の情報
 * @returns 実行結果
 */
export const patchCompositionsSLUnit = async (
  siteId: string,
  gatewayId: string,
  ouId: string,
  body: api.reqParameters.patchCompositionsSLUnit
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/sl-unit/${ouId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.patchCompositionsSLUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.patch(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 室外機の情報を削除します。
 * - DELETE /compositions/sites/{siteid}/gateway/{gatewayid}/sl-unit/{outdoorid}
 * @param siteId 削除したい室外機が登録されているサイトのID
 * @param gatewayId 削除したい室外機が登録されているゲートウェイのID
 * @param ouId 削除したい室外機のID
 * @returns 実行結果
 */
export const deleteCompositionsSLUnit = async (
  siteId: string,
  gatewayId: string,
  ouId: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/sl-unit/${ouId}`;

  //API要求パラメータセット
  const apiParams: api.req.deleteCompositionsSLUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.del(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 空調機の情報を返します。
 * - GET /compositions/sites/{siteid}/gateway/{gatewayid}/slif-unit
 * @param siteId 取得したい空調機が登録されているサイトのID
 * @param gatewayId 取得したい空調機が登録されているゲートウェイのID
 * @param query パラメータ
 * @returns 空調機の情報
 */
export const getCompositionsSLIFUnit = async (
  siteId: string,
  gatewayId: string,
  query?: api.reqParameters.getCompositionsSLIFUnit
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/slif-unit`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getCompositionsSLIFUnit;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.getCompositionsSLIFUnit = {
      'fields'     : query['fields'    ],
      'item-page'  : query['item-page' ],
      'max-result' : query['max-result'],
    };

    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
    };
  }

  //実行
  try {
    const response: api.res.getCompositionsSLIFUnit = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getCompositionsSLIFUnit = JSON.parse(
      JSON.stringify(response)
    );
    //prettier-ignore
    restoredResponse['unit-informations'] = response['unit-informations'].map(info => {
      return sanitizeRestoreObject(info) as api.resParameters.slIFUnitInformations;
    });

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定の空調機の情報を返します。
 * - GET /compositions/sites/{siteid}/gateway/{gatewayid}/slif-unit/{unitid}
 * @param siteId 取得したい空調機が登録されているサイトのID
 * @param gatewayId 取得したい空調機が登録されているゲートウェイのID
 * @param unitId 取得したい空調機のID
 * @returns 特定の空調機の情報
 */
export const getCompositionsSLIFUnitByUnitID = async (
  siteId: string,
  gatewayId: string,
  unitId: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/slif-unit/${unitId}`;

  //API要求パラメータセット
  const apiParams: api.req.getCompositionsSLIFUnitByUnitID = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行
  try {
    const response: api.res.getCompositionsSLIFUnitByUnitID = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getCompositionsSLIFUnitByUnitID;
  } catch (error) {
    throw error;
  }
};

/**
 * 空調機を追加します。
 * - POST /compositions/sites/{siteid}/gateway/{gatewayid}/slif-unit
 * @param siteId 追加したい空調機が登録されているサイトのID
 * @param gatewayId 追加したい空調機が登録されているゲートウェイのID
 * @param body 追加したい空調機の情報
 * @returns 追加した空調機の情報
 */
export const postCompositionsSLIFUnit = async (
  siteId: string,
  gatewayId: string,
  body: api.reqParameters.postCompositionsSLIFUnit
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/slif-unit`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.postCompositionsSLIFUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行
  try {
    const response: api.res.postCompositionsSLIFUnit = await API.post(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.postCompositionsSLIFUnit;
  } catch (error) {
    throw error;
  }
};

/**
 * 空調機の情報を修正します。
 * - PUT /compositions/sites/{siteid}/gateway/{gatewayid}/slif-unit/{unitid}
 * @param siteId 修正したい空調機が登録されているサイトのID
 * @param gatewayId 修正したい空調機が登録されているゲートウェイのID
 * @param unitId 修正したい空調機のID
 * @param body 修正した空調機の情報
 * @returns 実行結果
 */
export const putCompositionsSLIFUnit = async (
  siteId: string,
  gatewayId: string,
  unitId: string,
  body: api.reqParameters.putCompositionsSLIFUnit
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/slif-unit/${unitId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.putCompositionsSLIFUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.put(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 空調機の情報を削除します。
 * - DELETE /compositions/sites/{siteid}/gateway/{gatewayid}/slif-unit/{unitid}
 * @param siteId 削除したい空調機が登録されているサイトのID
 * @param gatewayId 削除したい空調機が登録されているゲートウェイのID
 * @param unitId 削除したい空調機のID
 * @returns 実行結果
 */
export const deleteCompositionsSLIFUnit = async (
  siteId: string,
  gatewayId: string,
  unitId: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/slif-unit/${unitId}`;

  //API要求パラメータセット
  const apiParams: api.req.deleteCompositionsSLIFUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.del(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 空調機の情報を一括取得します。
 * - GET /compositions/sites/{siteid}/gateway/{gatewayid}/all-unit
 * @param siteId 取得したい空調機が登録されているサイトのID
 * @param gatewayId 取得したい空調機が登録されているゲートウェイのID
 * @param query 取得したい出力内容　未入力の場合'current-setting'が渡されたとして処理される
 * @returns 追加した空調機の情報
 */
export const getCompositionsAllUnit = async (
  siteID: string,
  gatewayID: string,
  query?: api.reqParameters.getCompositionsSLAllUnit
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteID}/gateway/${gatewayID}/all-unit`;

  //API要求パラメータセット
  const apiParams: api.req.getCompositionsSLAllUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    queryStringParameters: query,
  };

  //実行
  try {
    const response = await API.get(apiName, path, apiParams);
    //サニタイズされた文字をもとに戻す
    return sanitizeRestore(response);
  } catch (error) {
    throw error;
  }
};

/**
 * 空調機を一括登録します。
 * - POST /compositions/sites/{siteid}/gateway/{gatewayid}/all-unit
 * @param siteId 追加したい空調機が登録されているサイトのID
 * @param gatewayId 追加したい空調機が登録されているゲートウェイのID
 * @param body 追加したい空調機の情報
 * @returns 追加した空調機の情報
 */
export const postCompositionsAllUnit = async (
  siteId: string,
  gatewayId: string,
  body: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/all-unit`;

  //API要求パラメータセット
  const apiParams: api.req.postCompositionsAllUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: body,
  };

  //実行
  try {
    const response: api.res.postCompositionsAllUnit = await API.post(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.postCompositionsAllUnit;
  } catch (error) {
    throw error;
  }
};

/**
 * 熱源機の情報を返します。
 * - GET /compositions/sites/{siteid}/gateway/{gatewayid}/rt-unit
 * @param siteId 取得したい熱源機が登録されているサイトのID
 * @param gatewayId 取得したい熱源機が登録されているゲートウェイのID
 * @param query パラメータ
 * @returns 熱源機の情報
 */
export const getCompositionsRTUnit = async (
  siteId: string,
  gatewayId: string,
  query?: api.reqParameters.getCompositionsRTUnit
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/rt-unit`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getCompositionsRTUnit;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.getCompositionsRTUnit = {
      'fields'     : query['fields'    ],
      'item-page'  : query['item-page' ],
      'max-result' : query['max-result'],
    };

    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
    };
  }

  //実行
  try {
    const response: api.res.getCompositionsRTUnit = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getCompositionsRTUnit = JSON.parse(
      JSON.stringify(response)
    );
    //prettier-ignore
    restoredResponse['unit-informations'] = response['unit-informations'].map(info => {
      return sanitizeRestoreObject(info) as api.resParameters.rtUnitInformations;
    });

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定の熱源機の情報を返します。
 * - GET /compositions/sites/{siteid}/gateway/{gatewayid}/rt-unit/{unitid}
 * @param siteId 取得したい熱源機が登録されているサイトのID
 * @param gatewayId 取得したい熱源機が登録されているゲートウェイのID
 * @param unitId 取得したい熱源機のID
 * @returns 特定の熱源機の情報
 */
export const getCompositionsRTUnitByUnitID = async (
  siteId: string,
  gatewayId: string,
  unitId: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/rt-unit/${unitId}`;

  //API要求パラメータセット
  const apiParams: api.req.getCompositionsRTUnitByUnitID = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行
  try {
    const response: api.res.getCompositionsRTUnitByUnitID = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getCompositionsRTUnitByUnitID;
  } catch (error) {
    throw error;
  }
};

/**
 * 熱源機を追加します。
 * - POST /compositions/sites/{siteid}/gateway/{gatewayid}/rt-unit
 * @param siteId 追加したい熱源機が登録されているサイトのID
 * @param gatewayId 追加したい熱源機が登録されているゲートウェイのID
 * @param body 追加したい熱源機の情報
 * @returns 追加した熱源機の情報
 */
export const postCompositionsRTUnit = async (
  siteId: string,
  gatewayId: string,
  body: api.reqParameters.postCompositionsRTUnit
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/rt-unit`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.postCompositionsRTUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行
  try {
    const response: api.res.postCompositionsRTUnit = await API.post(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.postCompositionsRTUnit;
  } catch (error) {
    throw error;
  }
};

/**
 * 熱源機の情報をまとめて修正します。
 * - PUT /compositions/sites/{siteid}/gateway/{gatewayid}/rt-unit/{unitid}
 * @param siteId 修正したい熱源機が登録されているサイトのID
 * @param gatewayId 修正したい熱源機が登録されているゲートウェイのID
 * @param unitId 修正したい熱源機のID
 * @param body 修正した熱源機の情報
 * @returns 実行結果
 */
export const putCompositionsRTUnit = async (
  siteId: string,
  gatewayId: string,
  unitId: string,
  body: api.reqParameters.putCompositionsRTUnit
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/rt-unit/${unitId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.putCompositionsRTUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.put(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 熱源機の情報を個別に修正します。
 * - PATCH /compositions/sites/{siteid}/gateway/{gatewayid}/rt-unit/{unitid}
 * @param siteId 修正したい熱源機が登録されているサイトのID
 * @param gatewayId 修正したい熱源機が登録されているゲートウェイのID
 * @param unitId 修正したい熱源機のID
 * @param body 修正した室内外機の情報
 * @returns 実行結果
 */
export const patchCompositionsRTUnit = async (
  siteId: string,
  gatewayId: string,
  unitId: string,
  body: api.reqParameters.patchCompositionsRTUnit
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/rt-unit/${unitId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));

  const apiParams: api.req.patchCompositionsRTUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.patch(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 熱源機の情報を削除します。
 * - DELETE /compositions/sites/{siteid}/gateway/{gatewayid}/rt-unit/{unitid}
 * @param siteId 削除したい熱源機が登録されているサイトのID
 * @param gatewayId 削除したい熱源機が登録されているゲートウェイのID
 * @param unitId 削除したい熱源機のID
 * @returns 実行結果
 */
export const deleteCompositionsRTUnit = async (
  siteId: string,
  gatewayId: string,
  unitId: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/rt-unit/${unitId}`;

  //API要求パラメータセット
  const apiParams: api.req.deleteCompositionsRTUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.del(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 室内外機の情報を返します。
 * - GET /compositions/sites/{siteid}/gateway/{gatewayid}/modbus-unit
 * @param siteId 取得したい室内外機が登録されているサイトのID
 * @param gatewayId 取得したい室内外機が登録されているゲートウェイのID
 * @param query パラメータ
 * @returns 室内外機の情報
 */
export const getCompositionsMoubusUnit = async (
  siteId: string,
  gatewayId: string,
  query?: api.reqParameters.getCompositionsMoubusUnit
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/modbus-unit`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getCompositionsMoubusUnit;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.getCompositionsMoubusUnit = {
      'fields'     : query['fields'    ],
      'item-page'  : query['item-page' ],
      'max-result' : query['max-result'],
    };

    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
    };
  }

  //実行
  try {
    const response: api.res.getCompositionsMoubusUnit = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getCompositionsMoubusUnit = JSON.parse(
      JSON.stringify(response)
    );

    //prettier-ignore
    restoredResponse['outdoor-unit-informations'] = response['outdoor-unit-informations'].map(info => {
      return sanitizeRestoreObject(info) as api.resParameters.modbusUnitInformations;
    });

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定の室外機の情報を返します。
 * - GET /compositions/sites/{siteid}/gateway/{gatewayid}/modbus-unit/{outdoorid}
 * @param siteId 取得したい室内外機が登録されているサイトのID
 * @param gatewayId 取得したい室内外機が登録されているゲートウェイのID
 * @param ouId 取得したい室外機のID
 * @returns 特定の室外機の情報
 */
export const getCompositionsMoubusUnitByOUID = async (
  siteId: string,
  gatewayId: string,
  ouId: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/modbus-unit/${ouId}`;

  //API要求パラメータセット
  const apiParams: api.req.getCompositionsMoubusUnitByOUID = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行
  try {
    const response: api.res.getCompositionsMoubusUnitByOUID = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getCompositionsMoubusUnitByOUID;
  } catch (error) {
    throw error;
  }
};

/**
 * 室外機を追加します。
 * - POST /compositions/sites/{siteid}/gateway/{gatewayid}/modbus-unit
 * @param siteId 追加したい室外機が登録されているサイトのID
 * @param gatewayId 追加したい室外機が登録されているゲートウェイのID
 * @param body 追加したい室外機の情報
 * @returns 追加した室外機の情報
 */
export const postCompositionsMoubusUnit = async (
  siteId: string,
  gatewayId: string,
  body: api.reqParameters.postCompositionsMoubusUnit
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/modbus-unit`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.postCompositionsMoubusUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行
  try {
    const response: api.res.postCompositionsMoubusUnit = await API.post(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.postCompositionsMoubusUnit;
  } catch (error) {
    throw error;
  }
};

/**
 * 室内外機の情報を修正します。
 * - PATCH /compositions/sites/{siteid}/gateway/{gatewayid}/modbus-unit/{outdoorid}
 * @param siteId 修正したい室内外機が登録されているサイトのID
 * @param gatewayId 修正したい室内外機が登録されているゲートウェイのID
 * @param ouId 修正したい室外機のID
 * @param body 修正した室内外機の情報
 * @returns 実行結果
 */
export const patchCompositionsMoubusUnit = async (
  siteId: string,
  gatewayId: string,
  ouId: string,
  body: api.reqParameters.patchCompositionsMoubusUnit
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/modbus-unit/${ouId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.patchCompositionsMoubusUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.patch(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 室外機の情報を削除します。
 * - DELETE /compositions/sites/{siteid}/gateway/{gatewayid}/modbus-unit/{outdoorid}
 * @param siteId 削除したい室外機が登録されているサイトのID
 * @param gatewayId 削除したい室外機が登録されているゲートウェイのID
 * @param ouId 削除したい室外機のID
 * @returns 実行結果
 */
export const deleteCompositionsMoubusUnit = async (
  siteId: string,
  gatewayId: string,
  ouId: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/gateway/${gatewayId}/modbus-unit/${ouId}`;

  //API要求パラメータセット
  const apiParams: api.req.deleteCompositionsMoubusUnit = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.del(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * グループの情報を返します。
 * - GET /compositions/sites/{siteid}/groups
 * @param siteId 取得したいグループが登録されているサイトのID
 * @param query パラメータ
 * @returns グループの情報
 */
export const getCompositionsGroups = async (
  siteId: string,
  query?: api.reqParameters.getCompositionsGroups
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/groups`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getCompositionsGroups;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.getCompositionsGroups = {
      'fields'     : query['fields'    ],
      'item-page'  : query['item-page' ],
      'max-result' : query['max-result'],
    };

    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
    };
  }

  //実行
  try {
    const response: api.res.getCompositionsGroups = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getCompositionsGroups;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のグループの情報を返します。
 * - GET /compositions/sites/{siteid}/groups/{groupid}
 * @param siteId 取得したいグループが登録されているサイトのID
 * @param groupId 取得したいグループのID
 * @returns 特定のグループの情報
 */
export const getCompositionsGroupsByGroupID = async (
  siteId: string,
  groupId: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/groups/${groupId}`;

  //API要求パラメータセット
  const apiParams: api.req.getCompositionsGroupsByGroupID = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行
  try {
    const response: api.res.getCompositionsGroupsByGroupID = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getCompositionsGroupsByGroupID;
  } catch (error) {
    throw error;
  }
};

/**
 * グループを追加します。
 * @param siteId 追加したいグループが登録されているサイトのID
 * @param body 追加したいグループの情報
 * @returns 追加したグループの情報
 */
export const postCompositionsGroups = async (
  siteId: string,
  body: api.reqParameters.postCompositionsGroups
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/groups`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.postCompositionsGroups = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行
  try {
    const response: api.res.postCompositionsGroups = await API.post(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.postCompositionsGroups;
  } catch (error) {
    throw error;
  }
};

/**
 * グループの情報を修正します。
 * - PATCH /compositions/sites/{siteid}/groups/{groupid}
 * @param siteId 修正したいグループが登録されているサイトのID
 * @param groupId 修正したいグループのID
 * @param body 修正したグループの情報
 * @returns 実行結果
 */
export const patchCompositionsGroups = async (
  siteId: string,
  groupId: string,
  body: api.reqParameters.patchCompositionsGroups
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/groups/${groupId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.patchCompositionsGroups = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.patch(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * グループの情報を削除します。
 * - DELETE /compositions/sites/{siteid}/groups/{groupid}
 * @param siteId 削除したいグループが登録されているサイトのID
 * @param groupId 削除したいグループのID
 * @returns 実行結果
 */
export const deleteCompositionsGroups = async (
  siteId: string,
  groupId: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/groups/${groupId}`;

  //API要求パラメータセット
  const apiParams: api.req.deleteCompositionsGroups = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.del(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のグループのレイアウト画像を返します。
 * - GET /compositions/sites/{siteid}/groups/{groupid}/layout-image
 * @param siteId 取得したいレイアウト画像のグループが登録されているサイトのID
 * @param groupId 取得したいレイアウト画像のグループのID
 * @returns 特定のグループのレイアウト画像
 */
export const getCompositionsLayoutImage = async (
  siteId: string,
  groupId: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/groups/${groupId}/layout-image`;

  //API要求パラメータセット
  const apiParams: api.req.getCompositionsLayoutImage = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行
  try {
    const response: api.res.getCompositionsLayoutImage = await API.get(
      apiName,
      path,
      apiParams
    );

    //画像データのためサニタイズされていない
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のグループのレイアウト画像をアップロードします。
 * - POST /compositions/sites/{siteid}/groups/{groupid}/layout-image
 * @param siteId アップロードしたいレイアウト画像のグループが登録されているサイトのID
 * @param groupId アップロードしたいレイアウト画像のグループのID
 * @param body アップロードしたいレイアウト画像
 * @returns アップロードしたレイアウト画像
 */
export const postCompositionsLayoutImage = async (
  siteId: string,
  groupId: string,
  body: api.reqParameters.postCompositionsLayoutImage
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/groups/${groupId}/layout-image`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.postCompositionsLayoutImage = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行
  try {
    const response: api.res.postCompositionsLayoutImage = await API.post(
      apiName,
      path,
      apiParams
    );

    //画像データのためサニタイズされていない
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のグループのレイアウト画像を削除します。
 * - DELETE /compositions/sites/{siteid}/groups/{groupid}/layout-image
 * @param siteId 削除したいレイアウト画像のグループが登録されているサイトのID
 * @param groupId 削除したいレイアウト画像のグループのID
 * @returns 実行結果
 */
export const deleteCompositionsLayoutImage = async (
  siteId: string,
  groupId: string
) => {
  const apiName = 'my-api';
  const path = `/compositions/sites/${siteId}/groups/${groupId}/layout-image`;

  //API要求パラメータセット
  const apiParams: api.req.deleteCompositionsLayoutImage = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.del(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * ユーザの情報を返します。
 * - GET /setting/user
 * @returns ユーザの情報
 */
export const getSettingUser = async () => {
  const apiName = 'my-api';
  const path = '/setting/user';

  //API要求パラメータセット
  const apiParams: api.req.getSettingUser = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行
  try {
    const response: api.res.getSettingUser = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getSettingUser;
  } catch (error) {
    throw error;
  }
};

/**
 * ユーザの情報を修正します。
 * - PATCH /setting/user
 * @param body 修正したユーザの情報
 * @returns 実行結果
 */
export const patchSettingUser = async (
  body: api.reqParameters.patchSettingUser
) => {
  const apiName = 'my-api';
  const path = '/setting/user';

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.patchSettingUser = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.patch(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * ユーザの情報を削除します。
 * - DELETE /setting/user
 * @returns 実行結果
 */
export const deleteSettingUser = async () => {
  const apiName = 'my-api';
  const path = '/setting/user';

  //API要求パラメータセット
  const apiParams: api.req.deleteSettingUser = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.del(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * サイトの権限を付与されているユーザの情報を返します。
 * - GET /setting/sites/{siteid}
 * @param siteId 取得したいサイトのID
 * @param query パラメータ
 * @returns サイトの権限
 */
export const getSettingSites = async (
  siteId: string,
  query?: api.reqParameters.getSettingSites
) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getSettingSites;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.getSettingSites = {
      'item-page'  : query['item-page' ],
      'max-result' : query['max-result'],
    };

    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
    };
  }

  //実行
  try {
    const response: api.res.getSettingSites = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getSettingSites = JSON.parse(
      JSON.stringify(response)
    );
    //prettier-ignore
    restoredResponse['users-authority'] = response['users-authority'].map(info => {
      return sanitizeRestoreObject(info) as api.resParameters.userAuthority;
    });

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * サイトの権限を付与します。
 * - POST /setting/sites/{siteid}
 * @param siteId 付与したいサイトのID
 * @param body 付与したいユーザの情報
 * @returns 追加したユーザの情報
 */
export const postSettingSites = async (
  siteId: string,
  body: api.reqParameters.postSettingSites
) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.postSettingSites = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行
  try {
    const response: api.res.postSettingSites = await API.post(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.postSettingSites;
  } catch (error) {
    throw error;
  }
};

/**
 * ユーザに付与されているサイトの権限を修正します。
 * - PATCH /setting/sites/{siteid}
 * @param siteId 修正したいサイトのID
 * @param body 修正したサイトの権限
 * @returns 実行結果
 */
export const patchSettingSites = async (
  siteId: string,
  body: api.reqParameters.patchSettingSites
) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.patchSettingSites = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.patch(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * ユーザに付与されているサイトの権限を削除します。
 * - DELETE /setting/sites/{siteid}
 * @param siteId 削除したいサイトのID
 * @param body 削除したいユーザ情報
 * @returns 実行結果
 */
export const deleteSettingSites = async (
  siteId: string,
  body: api.reqParameters.deleteSettingSites
) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.deleteSettingSites = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.del(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * サイトのロジック設定情報を返します。
 * - GET /setting/sites/{siteid}/logic
 * @param siteId 取得したいサイトのID
 * @returns サイトのロジック設定情報。
 */
export const getSettingLogic = async (siteId: string) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}/logic`;

  //API要求パラメータセット
  const apiParams: api.req.getSettingLogic = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行
  try {
    const response: api.res.getSettingLogic = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getSettingLogic;
  } catch (error) {
    throw error;
  }
};

/**
 * サイトのロジック設定を修正します。
 * - PUT /setting/sites/{siteid}/logic
 * @param siteId 修正するサイトのID
 * @param body 修正したロジックの情報
 * @returns 実行結果
 */
export const putSettingLogic = async (
  siteId: string,
  body: api.reqParameters.putSettingLogic
) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}/logic`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.putSettingLogic = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.put(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * サイトの状態値を返します
 * - ログインしているユーザが権限を持っているサイトの機種別（空調機、Qton、Hyozan、HCCVQ）の
 * 運転中の台数・異常発生中の台数、 空調機の状態値（運転/停止・エラーコード・運転モード・室内温度・設定温度・風量）、
 * サイトの消費電力量（Wh）を日付順（降順）で返します。
 * - GET /states/sites
 * @param query {apiReqParameters.getStatesSites} パラメータ
 * @returns サイトの状態値
 */
export const getStatesSites = async (
  query: api.reqParameters.getStatesSites
) => {
  const apiName = 'my-api';
  const path = '/states/sites';

  //API要求クエリセット
  //prettier-ignore
  const apiQuery: api.reqParameters.getStatesSites = {
    'fields'        : query['fields'      ],
    'site-id'       : query['site-id'     ],
    'after-time'    : query['after-time'  ],
    'before-time'   : query['before-time' ],
    'every-minute'  : query['every-minute'],
    'max-result'    : query['max-result'  ],
  };

  //API要求パラメータセット（クエリのundefinedは取り除く）
  const apiParams: api.req.getStatesSites = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    queryStringParameters: _delUndefinedObjFunc(apiQuery),
  };

  //実行
  try {
    const response: api.res.getStatesSites = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getStatesSites = JSON.parse(
      JSON.stringify(response)
    );
    restoredResponse['site-states'] = response['site-states'].map(state => {
      return sanitizeRestoreObject(state) as api.resParameters.siteState;
    });

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のサイトの状態値を返します
 * - 特定のサイトの機種別（空調機、Qton、Hyozan、HCCVQ）の運転中の台数・異常発生中の台数、
 * 空調機の状態値（運転/停止・エラーコード・運転モード・室内温度・設定温度・風量）、
 * サイトの消費電力量（Wh）を日付順（降順）で返します。
 * - GET /states/sites/{siteid}
 * @param siteId 取得するサイトのID
 * @param query パラメータ
 * @returns 特定のサイトの状態値
 */
export const getStatesSitesBySiteID = async (
  siteId: string,
  query?: api.reqParameters.getStatesSitesBySiteID
) => {
  const apiName = 'my-api';
  const path = `/states/sites/${siteId}`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getStatesSitesBySiteID;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.getStatesSitesBySiteID = {
      'fields'        : query['fields'      ],
      'after-time'    : query['after-time'  ],
      'before-time'   : query['before-time' ],
      'every-minute'  : query['every-minute'],
      'max-result'    : query['max-result'  ],
    };

    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
    };
  }

  //実行
  try {
    const response: api.res.getStatesSitesBySiteID = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getStatesSitesBySiteID;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のサイトの省エネ設定を取得します。
 * - GET /setting/sites/{siteid}/saving-energy
 * @param siteId サイトのID
 * @returns サイトの省エネ設定
 */
export const getSettingSavingEnergy = async (siteId: string) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}/saving-energy`;

  //API要求パラメータセット
  const apiParams: api.req.getSettingSavingEnergy = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };
  //実行
  try {
    const response: api.res.getSettingSavingEnergy = await API.get(
      apiName,
      path,
      apiParams
    );

    let restoredResponse: api.res.getSettingSavingEnergy = JSON.parse(
      JSON.stringify(response)
    );

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};
/**
 * 特定のサイトの省エネ設定を修正します。
 * - PUT /setting/sites/{siteid}/saving-energy
 * @param siteId サイトのID
 * @returns 実行結果
 */
export const putSettingSavingEnergy = async (
  siteId: string,
  body: api.reqParameters.putSettingSavingEnergy
) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}/saving-energy`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.putSettingSavingEnergy = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.put(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};
/**
 * 特定のサイトのフロア単位の状態値を返します。
 * - 特定のサイトのフロア別の運転中の台数・異常発生中の台数、
 * 空調機の状態値（運転/停止・エラーコード・運転モード・室内温度・設定温度・風量・消費電力量（Wh）・室外温度）を
 * 日付順（降順）で返します。
 * - GET /states/sites/{siteid}/floors
 * @param siteId 取得するサイトのID
 * @param query {apiReqParameters.getStatesSitesFloors} パラメータ
 * @returns
 */
export const getStatesSitesFloors = async (
  siteId: string,
  query?: api.reqParameters.getStatesSitesFloors
) => {
  const apiName = 'my-api';
  const path = `/states/sites/${siteId}/floors`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getStatesSitesFloors;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.getStatesSitesFloors = {
      'fields'        : query['fields'      ],
      'after-time'    : query['after-time'  ],
      'before-time'   : query['before-time' ],
      'every-minute'  : query['every-minute'],
      'max-result'    : query['max-result'  ],
    };

    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
    };
  }

  //実行
  try {
    const response: api.res.getStatesSitesFloors = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getStatesSitesFloors = JSON.parse(
      JSON.stringify(response)
    );
    restoredResponse['floor-states'] = response['floor-states'].map(state => ({
      date: sanitizeRestore(state.date),
      states: state.states.map(floorState => {
        return sanitizeRestoreObject(
          floorState
        ) as api.resParameters.floorStates;
      }),
    }));

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のサイトのグループ単位の状態値を返します。
 * - 特定のサイトのグループ別の運転中の台数・異常発生中の台数、 空調機の状態値（運転/停止・エラーコード・運転モード・室内温度・設定温度・風量・消費電力量（Wh）・室外温度）を 日付順（降順）で返します。
 * - GET /states/sites/{siteid}/groups
 * @param siteId 取得するサイトのID
 * @param query {api.reqParameters.getStatesSitesGroups} パラメータ
 * @returns
 */
export const getStatesSitesGroups = async (
  siteId: string,
  query?: api.reqParameters.getStatesSitesGroups
) => {
  const apiName = 'my-api';
  const path = `/states/sites/${siteId}/groups`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getStatesSitesGroups;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.getStatesSitesGroups = {
      'fields'        : query['fields'      ],
      'after-time'    : query['after-time'  ],
      'before-time'   : query['before-time' ],
      'every-minute'  : query['every-minute'],
      'max-result'    : query['max-result'  ],
    };

    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
    };
  }

  //実行
  try {
    const response: api.res.getStatesSitesGroups = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getStatesSitesGroups = JSON.parse(
      JSON.stringify(response)
    );
    restoredResponse['group-states'] = response['group-states'].map(state => ({
      date: sanitizeRestore(state.date),
      states: state.states.map(groupState => {
        return sanitizeRestoreObject(
          groupState
        ) as api.resParameters.groupStates;
      }),
    }));

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のサイトのSL通信設定の空調機単位の状態値を返します。
 * - 特定のサイトの状態値（運転/停止・エラーコード・運転モード・室内温度・設定温度・風量・消費電力量（Wh））をSL通信設定の空調機単位の日付順（降順）で返します。
 * - GET /states/sites/{siteid}/sl
 * @param siteId 取得するサイトのID
 * @param query {api.reqParameters.getStatesSitesSl} パラメータ
 * @returns
 */
export const getStatesSitesSl = async (
  siteId: string,
  query?: api.reqParameters.getStatesSitesSl
) => {
  const apiName = 'my-api';
  const path = `/states/sites/${siteId}/sl`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getStatesSitesSl;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.getStatesSitesSl = {
      'fields'        : query['fields'      ],
      'group-id'      : query['group-id'    ],
      'after-time'    : query['after-time'  ],
      'before-time'   : query['before-time' ],
      'every-minute'  : query['every-minute'],
      'max-result'    : query['max-result'  ],
    };

    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
    };
  }

  //実行
  try {
    const response: api.res.getStatesSitesSl = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getStatesSitesSl = JSON.parse(
      JSON.stringify(response)
    );
    restoredResponse['sl-states'] = response['sl-states'].map(state => ({
      date: sanitizeRestore(state.date),
      states: state.states.map(slState => {
        return sanitizeRestoreObject(slState) as api.resParameters.slStates;
      }),
    }));

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のサイトの熱源機単位の状態値を返します。
 * - 特定のサイトの状態値（機種によって異なります）を熱源機単位の日付順（降順）で返します。
 * - GET /states/sites/{siteid}/rt
 * @param siteId 取得するサイトのID
 * @param query パラメータ
 * @returns 特定のサイトの熱源機単位の状態値
 */
export const getStatesSitesRt = async (
  siteId: string,
  query?: api.reqParameters.getStatesSitesRt
) => {
  const apiName = 'my-api';
  const path = `/states/sites/${siteId}/rt`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getStatesSitesRt;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.getStatesSitesRt = {
      'fields'        : query['fields'      ],
      'group-id'      : query['group-id'    ],
      'after-time'    : query['after-time'  ],
      'before-time'   : query['before-time' ],
      'every-minute'  : query['every-minute'],
      'max-result'    : query['max-result'  ],
    };

    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
    };
  }

  //実行
  try {
    const response: api.res.getStatesSitesRt = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getStatesSitesRt = JSON.parse(
      JSON.stringify(response)
    );
    restoredResponse['rt-states'] = response['rt-states'].map(state => ({
      'date': sanitizeRestore(state.date),
      'outdoor-unit-states': state['outdoor-unit-states'].map(rtState => {
        return sanitizeRestoreObject(rtState) as api.resParameters.rtOUStates;
      }),
      'indoor-unit-states': state['indoor-unit-states'].map(rtState => {
        return sanitizeRestoreObject(rtState) as api.resParameters.rtIUStates;
      }),
    }));

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のサイトのModbus通信設定の空調機単位の状態値を返します。
 * - 特定のサイトの状態値をModbus通信設定の空調機単位の日付順（降順）で返します。
 * - GET /states/sites/{siteid}/modbus
 * @param siteId 取得するサイトのID
 * @param query パラメータ
 * @returns 特定のサイトのModbus通信設定の空調機単位の状態値
 */
export const getStatesSitesModbus = async (
  siteId: string,
  query?: api.reqParameters.getStatesSitesModbus
) => {
  const apiName = 'my-api';
  const path = `/states/sites/${siteId}/modbus`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getStatesSitesModbus;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.getStatesSitesModbus = {
      'fields'        : query['fields'      ],
      'group-id'      : query['group-id'    ],
      'after-time'    : query['after-time'  ],
      'before-time'   : query['before-time' ],
      'every-minute'  : query['every-minute'],
      'max-result'    : query['max-result'  ],
    };

    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
    };
  }

  //実行
  try {
    const response: api.res.getStatesSitesModbus = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getStatesSitesModbus = JSON.parse(
      JSON.stringify(response)
    );
    restoredResponse['modbus-states'] = response['modbus-states'].map(
      state => ({
        'date': sanitizeRestore(state.date),
        'outdoor-unit-states': state['outdoor-unit-states'].map(ouState => {
          return sanitizeRestoreObject(
            ouState
          ) as api.resParameters.modbusOUStates;
        }),
        'indoor-unit-states': state['indoor-unit-states'].map(iuState => {
          return sanitizeRestoreObject(
            iuState
          ) as api.resParameters.modbusIUStates;
        }),
      })
    );

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のサイトへ指令をします。
 * - POST /commands/sites/{siteid}
 * @param siteId 指令したいサイトのID
 * @param body 指令したい内容
 * @param query パラメータ
 * @returns 指令した内容
 */
export const postCommandsSites = async (
  siteId: string,
  body: api.reqParameters.postCommandsSitesBody,
  query?: api.reqParameters.postCommandsSitesQuery
) => {
  const apiName = 'my-api';
  const path = `/commands/sites/${siteId}`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  const tempBody = JSON.parse(JSON.stringify(body));
  let apiParams: api.req.postCommandsSites;
  if (query === undefined) {
    apiParams = {
      headers: {
        'Authorization': `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
        'x-api-key': process.env.REACT_APP_apiKeyCommand,
      },
      body: tempBody,
    };
  } else {
    //API要求クエリセット
    //prettier-ignore
    const apiQuery: api.reqParameters.postCommandsSitesQuery = {
      'mode'   : query.mode,
      'target' : query.target,
    };

    apiParams = {
      headers: {
        'Authorization': `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
        'x-api-key': process.env.REACT_APP_apiKeyCommand,
      },
      queryStringParameters: _delUndefinedObjFunc(apiQuery),
      body: tempBody,
    };
  }

  //実行
  try {
    const response: api.res.postCommandsSites = await API.post(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.postCommandsSites;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のゲートウェイへ指令をします。
 * @param siteId 指令したいゲートウェイが登録されているサイトのID
 * @param gatewayId 指令したいゲートウェイのID
 * @param body 指令したい内容
 * @returns 指令した内容
 */
export const postCommandsGateway = async (
  siteId: string,
  gatewayId: string,
  body: api.reqParameters.postCommandsGateway
) => {
  const apiName = 'my-api';
  const path = `/commands/sites/${siteId}/gateway/${gatewayId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.postCommandsGateway = {
    headers: {
      'Authorization': `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
      'x-api-key': process.env.REACT_APP_apiKeyCommand,
    },
    body: tempBody,
  };

  //実行
  try {
    const response: api.res.postCommandsGateway = await API.post(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.postCommandsGateway;
  } catch (error) {
    throw error;
  }
};

/**
 * 異常・不調の情報を返します。
 * - GET /report/analysis/sites/{siteid}
 * @param siteId 取得したい異常・不調の情報が登録されているサイトのID
 * @param query パラメータ
 * @returns 異常・不調の情報
 */
export const getReportAnalysis = async (
  siteId: string,
  query: api.reqParameters.getReportAnalysis
) => {
  const apiName = 'my-api';
  const path = `/report/analysis/sites/${siteId}`;

  //API要求クエリセット
  //prettier-ignore
  const apiQuery: api.reqParameters.getReportAnalysis = {
    'after-time'     : query['after-time'],
    'before-time'    : query['before-time'],
    'max-result'     : query['max-result'],
  };

  //API要求パラメータセット（クエリのundefinedは取り除く）
  const apiParams: api.req.getReportAnalysis = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    queryStringParameters: _delUndefinedObjFunc(apiQuery),
  };

  //実行
  try {
    const response: api.res.getReportAnalysis = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getReportAnalysis = JSON.parse(
      JSON.stringify(response)
    );
    //prettier-ignore
    restoredResponse.conditions = response.conditions.map(info => {
      return sanitizeRestoreObject(info) as api.resParameters.conditions;
    });

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 異常・不調の情報の分析結果を修正します。
 * - PATCH /report/analysis/sites/{siteid}
 * @param siteId 修正したい異常・不調の情報が登録されているサイトのID
 * @param body 修正した異常・不調の情報
 * @returns 実行結果
 */
export const patchReportAnalysis = async (
  siteId: string,
  body: api.reqParameters.patchReportAnalysis
) => {
  const apiName = 'my-api';
  const path = `/report/analysis/sites/${siteId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.patchReportAnalysis = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.patch(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * レポートの一覧を返します。
 * - GET /report/states/sites/{siteid}
 * @param siteId 取得したいレポートが登録されているサイトのID
 * @returns レポートの一覧
 */
export const getReportStates = async (siteId: string) => {
  const apiName = 'my-api';
  const path = `/report/states/sites/${siteId}`;

  //API要求パラメータセット
  const apiParams: api.req.getReportStates = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行
  try {
    const response: api.res.getReportStates = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getReportStates;
  } catch (error) {
    throw error;
  }
};

/**
 * レポートをダウンロードするためのパスを返します。
 * @param siteId 取得したいレポートが登録されているサイトのID
 * @param body 取得したいレポート一覧
 * @returns ダウンロードするためのパス
 */
export const postReportStates = async (
  siteId: string,
  body: api.reqParameters.postReportStates
) => {
  const apiName = 'my-api';
  const path = `/report/states/sites/${siteId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.postReportStates = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行
  try {
    const response: api.res.postReportStates = await API.post(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.postReportStates;
  } catch (error) {
    throw error;
  }
};

/**
 * 定期レポートの一覧を返します。
 * - GET /report/periodic/sites/{siteid}
 * @param siteId 取得したいレポートが登録されているサイトのID
 * @returns レポートの一覧
 */
export const getReportPeriodic = async (siteId: string) => {
  const apiName = 'my-api';
  const path = `/report/periodic/sites/${siteId}`;

  //API要求パラメータセット
  const apiParams: api.req.getReportPeriodic = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行
  try {
    const response: api.res.getReportPeriodic = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getReportPeriodic;
  } catch (error) {
    throw error;
  }
};
/**
 * 定期レポートをダウンロードするためのパスを返します。
 * @param siteId 取得したいレポートが登録されているサイトのID
 * @param body 取得したい定期レポート一覧
 * @returns ダウンロードするためのパス
 */
export const postReportPeriodic = async (
  siteId: string,
  body: api.reqParameters.postReportPeriodic
) => {
  const apiName = 'my-api';
  const path = `/report/periodic/sites/${siteId}`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.postReportPeriodic = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行
  try {
    const response: api.res.postReportPeriodic = await API.post(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.postReportPeriodic;
  } catch (error) {
    throw error;
  }
};

/**
 * 課金開始画面のURLを取得します。
 * - GET /billing/sites/{siteid}/gateway/{gatewayid}/checkout
 * @param siteId サイトのID
 * @param gatewayId ゲートウェイのID
 * @returns 実行結果
 */
export const getBillingCheckout = async (siteId: string, gatewayId: string) => {
  const apiName = 'my-api';
  const path = `/billing/sites/${siteId}/gateway/${gatewayId}/checkout`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getBillingCheckout;
  apiParams = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };
  //実行
  try {
    const response: api.res.getBillingCheckout = await API.get(
      apiName,
      path,
      apiParams
    );

    const restoredResponse: api.res.getBillingCheckout = JSON.parse(
      JSON.stringify(response)
    );

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 課金設定画面のURLを取得します。
 * - GET /billing/sites/{siteid}/gateway/{gatewayid}/portal
 * @param siteId サイトのID
 * @param gatewayId ゲートウェイのID
 * @returns 実行結果
 */
export const getBillingCustomerPortal = async (
  siteId: string,
  gatewayId: string
) => {
  const apiName = 'my-api';
  const path = `/billing/sites/${siteId}/gateway/${gatewayId}/portal`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  const apiParams: api.req.getBillingCustomerPortal = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };
  //実行
  try {
    const response: api.res.getBillingCustomerPortal = await API.get(
      apiName,
      path,
      apiParams
    );

    const restoredResponse: api.res.getBillingCustomerPortal = JSON.parse(
      JSON.stringify(response)
    );

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * クーポンの適用状況を取得します。
 * - GET /billing/sites/{siteid}/gateway/{gatewayid}/coupon
 * @param siteId サイトのID
 * @param gatewayId ゲートウェイのID
 * @returns 実行結果
 */
export const getBillingCouponApplied = async (
  siteId: string,
  gatewayId: string
) => {
  const apiName = 'my-api';
  const path = `/billing/sites/${siteId}/gateway/${gatewayId}/coupon`;

  //API要求パラメータセット（クエリのundefinedは取り除く）
  let apiParams: api.req.getBillingCouponApplied;
  apiParams = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };
  //実行
  try {
    const response: api.res.getBillingCouponApplied = await API.get(
      apiName,
      path,
      apiParams
    );

    const restoredResponse: api.res.getBillingCouponApplied = JSON.parse(
      JSON.stringify(response)
    );

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * クーポンを適用します。
 * - POST /billing/sites/{siteid}/gateway/{gatewayid}/coupon
 * @param siteId サイトのID
 * @param gatewayId ゲートウェイのID
 * @param body クーポンコード
 * @returns 実行結果
 */
export const postBillingApplyCoupon = async (
  siteId: string,
  gatewayId: string,
  body: api.reqParameters.postBillingApplyCoupon
) => {
  const apiName = 'my-api';
  const path = `/billing/sites/${siteId}/gateway/${gatewayId}/coupon`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.postBillingApplyCoupon = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };
  //実行
  try {
    const response: api.res.postBillingApplyCoupon = await API.post(
      apiName,
      path,
      apiParams
    );

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * VAT番号を取得します。
 * - GET /billing/sites/{siteid}/gateway/{gatewayid}/tax-id
 * @param siteId サイトのID
 * @param gatewayId ゲートウェイのID
 * @param body VAT番号
 * @returns 実行結果
 */
export const getBillingSettingVATNumber = async (
  siteId: string,
  gatewayId: string
) => {
  const apiName = 'my-api';
  const path = `/billing/sites/${siteId}/gateway/${gatewayId}/tax-id`;

  //API要求パラメータセット
  const apiParams: api.req.getBillingSettingTaxID = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };
  //実行
  try {
    const response: api.res.getBillingSettingTaxID = await API.get(
      apiName,
      path,
      apiParams
    );

    const restoredResponse: api.res.getBillingSettingTaxID = JSON.parse(
      JSON.stringify(response)
    );

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * VAT番号を設定します。
 * - POST /billing/sites/{siteid}/gateway/{gatewayid}/tax-id
 * @param siteId サイトのID
 * @param gatewayId ゲートウェイのID
 * @param body VAT番号
 * @returns 実行結果
 */
export const postBillingSettingVATNumber = async (
  siteId: string,
  gatewayId: string,
  body: api.reqParameters.postBillingSettingTaxID
) => {
  const apiName = 'my-api';
  const path = `/billing/sites/${siteId}/gateway/${gatewayId}/tax-id`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.postBillingSettingTaxID = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };
  //実行
  try {
    const response: api.res.postBillingSettingTaxID = await API.post(
      apiName,
      path,
      apiParams
    );

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 課金顧客情報を修正します。
 * - PATCH /billing/user/customer
 * @param body 修正したい顧客情報
 *
 * @returns 実行結果
 */
export const patchBillingCustomer = async (
  body: api.reqParameters.patchBillingCustomer
) => {
  const apiName = 'my-api';
  const path = `/billing/user/customer`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.patchBillingCustomer = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };
  //実行
  try {
    const response: api.res.patchBillingCustomer = await API.patch(
      apiName,
      path,
      apiParams
    );

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 支払いユーザの譲渡先を取得します。
 * - GET /billing/sites/{siteid}/gateway/transfer
 * @param siteId サイトのID
 * @returns 実行結果
 */
export const getBillingAuthorizationTransfer = async (siteId: string) => {
  const apiName = 'my-api';
  const path = `/billing/sites/${siteId}/gateway/transfer`;

  //API要求パラメータセット
  let apiParams: api.req.getBillingAuthorizationTransfer;
  apiParams = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };
  //実行
  try {
    const response: api.res.getBillingAuthorizationTransfer = await API.get(
      apiName,
      path,
      apiParams
    );

    const restoredResponse: api.res.getBillingAuthorizationTransfer =
      JSON.parse(JSON.stringify(response));

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 支払いユーザの譲渡先を登録します。
 * - POST /billing/sites/{siteid}/gateway/{gatewayid}/transfer
 * @param siteId サイトのID
 * @param gatewayId ゲートウェイのID
 * @param body 譲渡先ユーザのID
 * @returns 実行結果
 */
export const postBillingAuthorizationTransfer = async (
  siteId: string,
  gatewayId: string,
  body: api.reqParameters.postBillingAuthorizationTransfer
) => {
  const apiName = 'my-api';
  const path = `/billing/sites/${siteId}/gateway/${gatewayId}/transfer`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  let apiParams: api.req.postBillingAuthorizationTransfer;
  apiParams = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };
  //実行
  try {
    const response: api.res.resultOnly = await API.post(
      apiName,
      path,
      apiParams
    );

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 支払いユーザの譲渡先を削除します。
 * - DELETE /billing/sites/{siteid}/gateway/{gatewayid}/transfer
 * @param siteId サイトのID
 * @param gatewayId ゲートウェイのID
 * @returns 実行結果
 */
export const deleteBillingAuthorizationTransfer = async (
  siteId: string,
  gatewayId: string
) => {
  const apiName = 'my-api';
  const path = `/billing/sites/${siteId}/gateway/${gatewayId}/transfer`;

  //API要求パラメータセット
  const apiParams: api.req.deleteBillingAuthorizationTransfer = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.del(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 支払いユーザの譲渡を受け入れます。
 * - POST /billing/sites/{siteid}/gateway/{gatewayid}/transfer/accept
 * @param siteId サイトのID
 * @param gatewayId ゲートウェイのID
 * @returns 実行結果
 */
export const postBillingAuthorizationTransferAccept = async (
  siteId: string,
  gatewayId: string
) => {
  const apiName = 'my-api';
  const path = `/billing/sites/${siteId}/gateway/${gatewayId}/transfer/accept`;

  //API要求パラメータセット
  let apiParams: api.req.postBillingAuthorizationTransferAccept;
  apiParams = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };
  //実行
  try {
    const response: api.res.resultOnly = await API.post(
      apiName,
      path,
      apiParams
    );

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * ユーザの管理するサイトのエラーを取得します。
 * - GET /states/sites/error
 * @param
 * @returns 実行結果
 */
export const getStatesErrorSites = async () => {
  const apiName = 'my-api';
  const path = '/states/sites/error';

  //API要求パラメータセット（クエリのundefinedは取り除く）
  const apiParams: api.req.getStatesErrorSites = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };

  //実行
  try {
    const response: api.res.getStatesErrorSites = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    let restoredResponse: api.res.getStatesErrorSites = JSON.parse(
      JSON.stringify(response)
    );
    restoredResponse['error-informations'] = response['error-informations'].map(
      (state: object) => {
        return sanitizeRestoreObject(
          state
        ) as api.resParameters.siteStatesError;
      }
    );

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のサイトの日毎の設定を取得します。
 * - GET /setting/sites/{siteid}/calender
 * @param siteId サイトのID
 * @returns サイトの日毎の設定
 */
export const getSiteCalender = async (siteId: string) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}/calender`;

  //API要求パラメータセット
  const apiParams: api.req.getSiteCalender = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };
  //実行
  try {
    const response: api.res.getSiteCalender = await API.get(
      apiName,
      path,
      apiParams
    );

    const restoredResponse: api.res.getSiteCalender = JSON.parse(
      JSON.stringify(response)
    );

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のサイトの日毎の設定を修正します。
 * - PUT /setting/sites/{siteid}/calender
 * @param siteId サイトのID
 * @returns 実行結果
 */
export const putSiteCalender = async (
  siteId: string,
  body: api.reqParameters.putSiteCalender
) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}/calender`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.putSiteCalender = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.put(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};
/**
 * 特定のサイトの標準設定を取得します。
 * - GET /setting/sites/{siteid}/calender/routine
 * @param siteId サイトのID
 * @returns サイトの標準設定
 */
export const getSiteCalenderRoutine = async (siteId: string) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}/calender/routine`;

  //API要求パラメータセット
  const apiParams: api.req.getSiteCalenderRoutine = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };
  //実行
  try {
    const response: api.res.getSiteCalenderRoutine = await API.get(
      apiName,
      path,
      apiParams
    );

    const restoredResponse: api.res.getSiteCalenderRoutine = JSON.parse(
      JSON.stringify(response)
    );
    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のサイトの標準設定を修正します。
 * - PUT /setting/sites/{siteid}/calender/routine
 * @param siteId サイトのID
 * @returns 実行結果
 */
export const putSiteCalenderRoutine = async (
  siteId: string,
  body: api.reqParameters.putSiteCalenderRoutine
) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}/calender/routine`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.putSiteCalenderRoutine = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.put(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のグループの当日スケジュールを取得します。
 * - GET /setting/sites/{siteid}/groups/{groupid}/schedule/today
 * @param siteId サイトのID
 * @param groupID グループのID
 *
 * @returns 当日スケジュール
 */
export const getTodaySchedule = async (siteId: string, groupID: string) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}/groups/${groupID}/schedule/today`;

  //API要求パラメータセット
  const apiParams: api.req.getTodaySchedule = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };
  //実行
  try {
    const response: api.res.getTodaySchedule = await API.get(
      apiName,
      path,
      apiParams
    );

    const restoredResponse: api.res.getTodaySchedule = JSON.parse(
      JSON.stringify(response)
    );

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のグループの当日スケジュールを修正します。
 * - PUT /setting/sites/{siteid}/groups/{groupid}/schedule/today
 * @param siteId サイトのID
 * @param groupId グループのID
 *
 * @returns 実行結果
 */
export const putTodaySchedule = async (
  siteId: string,
  groupId: string,
  body: api.reqParameters.putTodaySchedule
) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}/groups/${groupId}/schedule/today`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.putTodaySchedule = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.put(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のグループの特定日スケジュールを取得します。
 * - GET /setting/sites/{siteid}/groups/{groupid}/schedule/specific
 * @param siteId サイトのID
 * @param groupID グループのID
 * @param query 出力形式パラメータ
 * @returns 特定日スケジュール
 */
export const getDailySchedule = async (
  siteId: string,
  groupID: string,
  query?: api.reqParameters.scheduleFormat
) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}/groups/${groupID}/schedule/specific`;

  //API要求パラメータセット
  let apiParams: api.req.getDailySchedule;

  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
    };
  } else {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: query,
    };
  }

  //実行
  try {
    const response: api.res.getDailySchedule = await API.get(
      apiName,
      path,
      apiParams
    );

    const restoredResponse: api.res.getDailySchedule = JSON.parse(
      JSON.stringify(response)
    );

    return restoredResponse;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のグループの特定日スケジュールを修正します。
 * - PATCH /setting/sites/{siteid}/groups/{groupid}/schedule/specific
 * @param siteId サイトのID
 * @param groupId グループのID
 * @param body ボディデータ
 * @param query 出力形式パラメータ
 * @returns 実行結果
 */
export const patchDailySchedule = async (
  siteId: string,
  groupId: string,
  body: api.reqParameters.patchDailySchedule,
  query?: api.reqParameters.scheduleFormat
) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}/groups/${groupId}/schedule/specific`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  let apiParams: api.req.patchDailySchedule;
  if (query === undefined) {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      body: tempBody,
    };
  } else {
    apiParams = {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`,
      },
      queryStringParameters: query,
      body: tempBody,
    };
  }

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.patch(
      apiName,
      path,
      apiParams
    );

    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のサイトのデマンド設定情報を取得します。
 * - GET /setting/sites/{siteid}/demand
 * @param siteId サイトのID
 *
 * @returns デマンド設定情報
 */
export const getDemandSetting = async (siteId: string) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}/demand`;

  //API要求パラメータセット
  const apiParams: api.req.getDemandSetting = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };
  //実行
  try {
    const response: api.res.getDemandSetting = await API.get(
      apiName,
      path,
      apiParams
    );

    const restoredResponse: api.res.getDemandSetting = JSON.parse(
      JSON.stringify(response)
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(restoredResponse) as api.res.getDemandSetting;
  } catch (error) {
    throw error;
  }
};

/**
 * 特定のサイトのデマンド設定情報を修正します。
 * - PATCH /setting/sites/{siteid}/demand
 * @param siteId 修正したいデマンドが登録されているサイトのID
 * @param body 修正したデマンド設定の情報
 * @returns 実行結果
 */
export const patchDemandSetting = async (
  siteId: string,
  body: api.reqParameters.patchDemandSetting
) => {
  const apiName = 'my-api';
  const path = `/setting/sites/${siteId}/demand`;

  //API要求パラメータセット
  const tempBody = JSON.parse(JSON.stringify(body));
  const apiParams: api.req.patchDemandSetting = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: tempBody,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.patch(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 最新の天気の情報を返します。
 * @param query パラメータ
 * @returns 最新の天気の情報
 */
export const getWeatherCurrent = async (
  query: api.reqParameters.getWeatherCurrent
) => {
  const apiName = 'my-api';
  const path = '/weather/current';

  //API要求クエリセット
  //prettier-ignore
  const apiQuery = _delUndefinedObjFunc({
    'location'         : query.location,
    'language'         : query.language,
    'air-quality-data' : query['air-quality-data'],
  }) as api.reqParameters.getWeatherCurrent;

  //API要求パラメータセット
  const apiParams: api.req.getWeatherCurrent = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    queryStringParameters: apiQuery,
  };

  //実行
  try {
    const response: api.res.getWeatherCurrent = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getWeatherCurrent;
  } catch (error) {
    throw error;
  }
};

/**
 * 一致する場所を返します。
 * @param query パラメータ
 * @returns 一致する場所
 */
export const getWeatherSearch = async (
  query: api.reqParameters.getWeatherSearch
) => {
  const apiName = 'my-api';
  const path = '/weather/search';

  //API要求クエリセット
  const apiQuery = _delUndefinedObjFunc({
    query: query.query,
  }) as api.reqParameters.getWeatherSearch;

  //API要求パラメータセット
  const apiParams: api.req.getWeatherSearch = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    queryStringParameters: apiQuery,
  };

  //実行
  try {
    const response: api.res.getWeatherSearch = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getWeatherSearch;
  } catch (error) {
    throw error;
  }
};

/**
 * 生成済み・生成中の状態値データファイルの一覧を返します。
 * - GET /history/sites/{siteid}
 * @param siteId サイトのID
 * @returns 生成済み・生成中の状態値データファイルの一覧
 */
export const getHistorySitesBySiteID = async (siteId: string) => {
  const apiName = 'my-api';
  const path = `/history/sites/${siteId}`;

  //API要求パラメータセット
  const apiParams: api.req.getHistorySitesBySiteID = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
  };
  //実行
  try {
    const response: api.res.getHistorySitesBySiteID = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getHistorySitesBySiteID;
  } catch (error) {
    throw error;
  }
};

/**
 * 指定された期間の状態値データファイルのダウンロードパスを返します。
 * @param siteId 取得したいユニットが登録されているサイトのID
 * @param gatewayId 取得したいユニットが登録されているゲートウェイのID
 * @param unitId 取得したいユニットのID
 * @param query パラメータ
 * @returns 指定された期間の状態値データファイルのダウンロードパス
 */
export const getHistorySitesByUnitID = async (
  siteId: string,
  gatewayId: string,
  unitId: string,
  query: api.reqParameters.getHistorySitesByUnitID
) => {
  const apiName = 'my-api';
  const path = `/history/sites/${siteId}/gateway/${gatewayId}/unit/${unitId}`;

  //API要求クエリセット
  //prettier-ignore
  const apiQuery: api.reqParameters.getHistorySitesByUnitID = {
      'before-time' : query['before-time'],
      'after-time'  : query['after-time']
    };

  //API要求パラメータセット
  const apiParams: api.req.getHistorySitesByUnitID = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    queryStringParameters: apiQuery,
  };

  //実行
  try {
    const response: api.res.getHistorySitesByUnitID = await API.get(
      apiName,
      path,
      apiParams
    );

    //サニタイズされた文字をもとに戻す
    return sanitizeRestoreObject(
      JSON.parse(JSON.stringify(response))
    ) as api.res.getHistorySitesByUnitID;
  } catch (error) {
    throw error;
  }
};

/**
 * 指定された期間の状態値データファイルを作成します。
 * @param siteId 取得したいユニットが登録されているサイトのID
 * @param gatewayId 取得したいユニットが登録されているゲートウェイのID
 * @param unitId 取得したいユニットのID
 * @param query パラメータ
 * @returns 実行結果
 */
export const postHistorySitesByUnitID = async (
  siteId: string,
  gatewayId: string,
  unitId: string,
  query: api.reqParameters.postHistorySitesByUnitID
) => {
  const apiName = 'my-api';
  const path = `/history/sites/${siteId}/gateway/${gatewayId}/unit/${unitId}`;

  //API要求クエリセット
  //prettier-ignore
  const apiQuery: api.reqParameters.postHistorySitesByUnitID = {
      'before-time' : query['before-time'],
      'after-time'  : query['after-time']
    };

  //API要求パラメータセット
  const apiParams: api.req.postHistorySitesByUnitID = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    queryStringParameters: apiQuery,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.post(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * 指定された期間の状態値データファイルを削除します。
 * @param siteId 取得したいユニットが登録されているサイトのID
 * @param gatewayId 取得したいユニットが登録されているゲートウェイのID
 * @param unitId 取得したいユニットのID
 * @param query パラメータ
 * @returns 実行結果
 */
export const deleteHistorySitesByUnitID = async (
  siteId: string,
  gatewayId: string,
  unitId: string,
  query: api.reqParameters.deleteHistorySitesByUnitID
) => {
  const apiName = 'my-api';
  const path = `/history/sites/${siteId}/gateway/${gatewayId}/unit/${unitId}`;

  //API要求クエリセット
  //prettier-ignore
  const apiQuery: api.reqParameters.deleteHistorySitesByUnitID = {
    'before-time' : query['before-time'],
    'after-time'  : query['after-time']
  };

  //API要求パラメータセット
  const apiParams: api.req.deleteHistorySitesByUnitID = {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    queryStringParameters: apiQuery,
  };

  //実行（結果のみの応答値のためサニタイズされていない）
  try {
    const response: api.res.resultOnly = await API.del(
      apiName,
      path,
      apiParams
    );
    return response;
  } catch (error) {
    throw error;
  }
};
